import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata'

export interface SubscriptionPlan {
  id: string
  name: string
  description: string
  price: number
  currency: string
  interval: 'month' | 'year'
  features: string[]
  limitations: string[]
  recommended?: boolean
}

export interface Subscription {
  id: string
  planId: string
  planName: string
  status: 'active' | 'cancelled' | 'past_due' | 'trialing'
  currentPeriodStart: string
  currentPeriodEnd: string
  cancelAtPeriodEnd: boolean
  price: number
  currency: string
  interval: 'month' | 'year'
}

export interface UsageStats {
  templates: { used: number; limit: number }
  aiGenerations: { used: number; limit: number }
  storage: { used: number; limit: number }
  teamMembers: { used: number; limit: number }
}

export interface Invoice {
  id: string
  invoiceNumber: string
  date: string
  amount: number
  currency: string
  status: 'paid' | 'pending' | 'failed'
  downloadUrl?: string
}

export interface InvoicesODataResponse {
  value: Invoice[]
  '@odata.count'?: number
}

export interface PaymentMethod {
  id: string
  type: 'card' | 'bank_account'
  brand?: string
  last4: string
  expiryMonth?: number
  expiryYear?: number
  isDefault: boolean
}

export interface BillingAddress {
  company?: string
  line1: string
  line2?: string
  city: string
  state?: string
  postalCode: string
  country: string
}

export const billingService = {
  /**
   * Get available subscription plans
   */
  async getPlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await api.get<SubscriptionPlan[]>('/v1/billing/plans')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get current subscription
   */
  async getSubscription(): Promise<Subscription | null> {
    try {
      const response = await api.get<Subscription>('/v1/billing/subscription')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get usage statistics
   */
  async getUsage(): Promise<UsageStats> {
    try {
      const response = await api.get<UsageStats>('/v1/billing/usage')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get invoices with OData support
   */
  async getInvoices(params?: ODataQueryParams): Promise<InvoicesODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<InvoicesODataResponse | Invoice[]>(`/v1/billing/invoices${queryString}`)

      if (Array.isArray(response.data)) {
        return {
          value: response.data,
          '@odata.count': response.data.length
        }
      }
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Download invoice PDF
   */
  async downloadInvoice(invoiceId: string): Promise<Blob> {
    try {
      const response = await api.get<Blob>(`/v1/billing/invoices/${invoiceId}/download`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get payment methods
   */
  async getPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      const response = await api.get<PaymentMethod[]>('/v1/billing/payment-methods')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Add payment method
   */
  async addPaymentMethod(paymentMethodId: string): Promise<PaymentMethod> {
    try {
      const response = await api.post<PaymentMethod>('/v1/billing/payment-methods', {
        paymentMethodId,
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Set default payment method
   */
  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    try {
      await api.post(`/v1/billing/payment-methods/${paymentMethodId}/default`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Remove payment method
   */
  async removePaymentMethod(paymentMethodId: string): Promise<void> {
    try {
      await api.delete(`/v1/billing/payment-methods/${paymentMethodId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get billing address
   */
  async getBillingAddress(): Promise<BillingAddress | null> {
    try {
      const response = await api.get<BillingAddress>('/v1/billing/address')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Update billing address
   */
  async updateBillingAddress(address: BillingAddress): Promise<BillingAddress> {
    try {
      const response = await api.put<BillingAddress>('/v1/billing/address', address)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Subscribe to a plan
   */
  async subscribe(planId: string, interval: 'month' | 'year'): Promise<Subscription> {
    try {
      const response = await api.post<Subscription>('/v1/billing/subscribe', {
        planId,
        interval,
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Change subscription plan
   */
  async changePlan(planId: string): Promise<Subscription> {
    try {
      const response = await api.post<Subscription>('/v1/billing/change-plan', {
        planId,
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Cancel subscription
   */
  async cancelSubscription(): Promise<void> {
    try {
      await api.post('/v1/billing/cancel')
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Resume cancelled subscription
   */
  async resumeSubscription(): Promise<Subscription> {
    try {
      const response = await api.post<Subscription>('/v1/billing/resume')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Create checkout session for plan upgrade
   */
  async createCheckoutSession(planId: string, interval: 'month' | 'year'): Promise<{ sessionUrl: string }> {
    try {
      const response = await api.post<{ sessionUrl: string }>('/v1/billing/checkout', {
        planId,
        interval,
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get customer portal URL (Stripe)
   */
  async getCustomerPortalUrl(): Promise<{ portalUrl: string }> {
    try {
      const response = await api.post<{ portalUrl: string }>('/v1/billing/portal')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
